import { useGetTransactions } from 'api/payments/generated/fragmentPaymentsAPI';
import {
  PaymentViewLayout,
  PaymentViewLoading,
} from 'components/Layout/PaymentViewLayout';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import React, { Suspense, useCallback, useMemo } from 'react';
import { makePath } from 'utils/routes';
import { Link, useLocation } from 'wouter';

import { Amount } from '@fragment/ui/src/components/Amount/Amount';

type Props = {
  workspaceId: string;
  transactionId: string;
};

type Allocation = {
  invoice_id: string;
  amount: string;
  type: string;
  user: { id?: string; external_id?: string };
};

const TransactionDetailPageContent = ({
  workspaceId,
  transactionId,
}: Props): JSX.Element => {
  useDocumentTitle('Transaction Details');

  const [, setLocation] = useLocation();
  const { data, isLoading } = useGetTransactions({});

  // Find the current transaction
  const transaction = useMemo(() => {
    if (!data?.data) return undefined;
    return data.data.find((txn) => txn.id === transactionId);
  }, [data, transactionId]);

  const handleBack = useCallback(() => {
    if (transaction?.account.id) {
      setLocation(
        makePath(
          ['w', workspaceId, 'payments', 'accounts', transaction.account.id],
          {
            absolute: true,
          }
        )
      );
    } else {
      // Fallback to accounts list if no account info
      setLocation(
        makePath(['w', workspaceId, 'payments', 'transactions'], {
          absolute: true,
        })
      );
    }
  }, [workspaceId, transaction, setLocation]);

  if (isLoading) {
    return (
      <PaymentViewLayout title="Transaction Details">
        <div className="flex items-center justify-center py-f8 text-main-500">
          Loading transaction...
        </div>
      </PaymentViewLayout>
    );
  }

  if (!transaction) {
    return (
      <PaymentViewLayout title="Transaction Details">
        <div className="flex items-center justify-center py-f8 text-main-500">
          Transaction not found
        </div>
      </PaymentViewLayout>
    );
  }

  return (
    <PaymentViewLayout title="Transaction Details">
      <div className="flex flex-col min-h-f1 w-full h-full">
        <button
          type="button"
          onClick={handleBack}
          className="px-f2 py-f2 text-left text-main-500 hover:text-main border-b border-main-300"
        >
          ← Back to Account
        </button>
        <div className="p-f2 flex flex-col gap-y-f3">
          {/* Transaction Header */}
          <div className="flex flex-col gap-y-f1">
            <div className="text-main">{transaction.external_id}</div>
            <div className="text-main-500 text-small">ID: {transaction.id}</div>
          </div>

          {/* Transaction Details */}
          <div className="flex flex-col gap-y-f2 border-t border-main-300 pt-f2">
            <div className="flex flex-col gap-y-f1">
              <div className="text-main-500 text-small">Amount</div>
              <div className="text-main">
                <Amount
                  amount={transaction.amount}
                  currency={{
                    code: transaction.currency as 'USD' | 'EUR' | 'GBP',
                    name: transaction.currency,
                    precision: 2,
                  }}
                />
              </div>
            </div>

            <div className="flex flex-col gap-y-f1">
              <div className="text-main-500 text-small">Unallocated Amount</div>
              <div className="text-main">
                <Amount
                  amount={transaction.unallocated_amount}
                  currency={{
                    code: transaction.currency as 'USD' | 'EUR' | 'GBP',
                    name: transaction.currency,
                    precision: 2,
                  }}
                />
              </div>
            </div>

            <div className="flex flex-col gap-y-f1">
              <div className="text-main-500 text-small">Posted</div>
              <div className="text-main">
                {new Date(transaction.posted).toLocaleDateString()}
              </div>
            </div>
          </div>

          {/* Allocations */}
          <div className="flex flex-col gap-y-f2 border-t border-main-300 pt-f2">
            <div className="text-main">Allocations</div>
            {transaction.allocations.length === 0 ? (
              <div className="text-main-500 text-small">No allocations</div>
            ) : (
              <div className="flex flex-col gap-y-f2">
                {transaction.allocations.map((allocation: Allocation) => (
                  <Link
                    key={`${allocation.invoice_id}-${allocation.amount}`}
                    href={makePath(
                      [
                        'w',
                        workspaceId,
                        'payments',
                        'invoices',
                        allocation.invoice_id,
                      ],
                      { absolute: true }
                    )}
                  >
                    <div className="flex flex-col gap-y-f1 p-f2 border border-main-300 rounded hover:bg-main-100 cursor-pointer">
                      <div className="text-main-500 text-small">
                        Invoice: {allocation.invoice_id}
                      </div>
                      <div className="text-main">
                        Amount:{' '}
                        <Amount
                          amount={allocation.amount}
                          currency={{
                            code: transaction.currency as 'USD' | 'EUR' | 'GBP',
                            name: transaction.currency,
                            precision: 2,
                          }}
                        />
                      </div>
                      <div className="text-main-500 text-small">
                        Type: {allocation.type}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </PaymentViewLayout>
  );
};

export const TransactionDetailPage = ({
  workspaceId,
  transactionId,
}: Props): JSX.Element => (
  <Suspense
    fallback={<PaymentViewLoading message="Loading transaction details..." />}
  >
    <TransactionDetailPageContent
      workspaceId={workspaceId}
      transactionId={transactionId}
    />
  </Suspense>
);
